import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import EmailStep from "../../components/auth/EmailStep";
import OtpStep from "../../components/auth/OtpStep";
import AuthShell from "../../components/auth/AuthShell";
import { routeAfterVerify } from "../../components/auth/verifyRouting";
import { sendOtp, checkMember } from "../../services/authService";
import ClubSelectModal from "../../components/common/ClubSelectModal";

export default function LoginPage() {
  const location = useLocation();
  // The home-page login card hands off the typed email via router state so the
  // EmailStep field is prefilled here. It may also have already sent the OTP
  // (`otpSent`) — in which case we open straight on the OTP step so the user
  // doesn't press "Send OTP" again — or detected an unregistered email
  // (`notRegistered`), in which case we show the "No Account Found" popup.
  const initialEmail = location.state?.email || "";
  const [step, setStep] = useState(location.state?.otpSent ? "otp" : "email");
  const [email, setEmail] = useState(initialEmail);
  // Placeholder message shown after a registered user verifies their OTP.
  const [loginMessage, setLoginMessage] = useState("");
  // Set when the verified email has no account — we ask them to register
  // instead of logging in.
  const [notRegistered, setNotRegistered] = useState(Boolean(location.state?.notRegistered));
  // Set when a coach with 2+ clubs verifies — shows the "Select a Club" popup
  // right here on the login page instead of navigating to /select-club.
  const [clubSelectData, setClubSelectData] = useState(null);
  const navigate = useNavigate();

  const handleSendOtp = (sentEmail) => {
    setEmail(sentEmail);
    setStep("otp");
  };

  // Runs before the login OTP is sent (EmailStep `beforeSend`). Blocks OTPs for
  // unknown emails: only a real account — or a registration still in progress —
  // is allowed a code (the latter so the existing verify flow can resume it). A
  // genuinely unregistered email shows the "No Account Found" popup instead of
  // sending a code, and offers to go to registration.
  const handleBeforeSend = async (enteredEmail) => {
    try {
      const data = await checkMember(enteredEmail);
      if (data?.account_exists || data?.registration_in_progress) return true;
      setEmail(enteredEmail);
      setNotRegistered(true);
      return false;
    } catch {
      // If the check itself fails, fall back to the normal flow (send the OTP)
      // rather than blocking a possibly-valid login.
      return true;
    }
  };

  // Dismiss the "No Account Found" modal and keep the user on the login page
  // (return to the email step so they can try a different address).
  const closeNotRegistered = () => {
    setNotRegistered(false);
    setStep("email");
  };

  const handleVerify = (data) => {
    // `data` is the /verify-otp response ("account routing details"). The
    // routing decision is shared with the home-page login modal.
    routeAfterVerify(data, {
      email,
      navigate,
      onNotRegistered: () => setNotRegistered(true),
      onPlaceholder: (text) => setLoginMessage(text),
      onClubSelect: (payload) => setClubSelectData(payload),
    });
  };

  // ── Post-login placeholder (registered user) ──────────────────────────────
  if (loginMessage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex flex-col items-center justify-center m-0 px-8 sm:px-12 lg:px-16">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-green-50">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Login Successful</h2>
          <p className="text-sm text-gray-500 mb-1">Signed in as</p>
          <p className="text-sm font-semibold text-blue-700 mb-5">{email}</p>
          <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-4 mb-5">
            <p className="text-base font-semibold text-blue-700">{loginMessage}</p>
          </div>
          <button
            onClick={() => {
              setLoginMessage("");
              setStep("email");
              setEmail("");
            }}
            className="w-full text-blue-600 font-semibold text-sm hover:underline"
          >
            Back to Login
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-8">
          © {new Date().getFullYear()} University of Sri Jayewardenepura
        </p>
      </div>
    );
  }

  return (
    <AuthShell>
      {/* Login form (right panel) */}
      <div className="w-full flex flex-col items-center">
        {/* Step indicator */}
        <div className="flex items-center gap-2 sm:gap-4 mb-8 w-full max-w-sm sm:max-w-none justify-center">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold border-2 ${
              step === "email"
                ? "bg-blue-700 text-white border-blue-700"
                : "bg-green-500 text-white border-green-500"
            }`}>
              {step === "otp" ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : "1"}
            </div>
            <span className={`text-sm sm:text-lg font-semibold ${step === "email" ? "text-blue-700" : "text-gray-500"}`}>
              Email OTP Login
            </span>
          </div>

          <div className={`h-px w-12 ${step === "otp" ? "bg-blue-600" : "bg-gray-300"}`} />

          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold border-2 ${
              step === "otp"
                ? "bg-blue-700 text-white border-blue-700"
                : "bg-gray-200 text-gray-500 border-gray-200"
            }`}>
              2
            </div>
            <span className={`text-sm sm:text-lg font-semibold ${step === "otp" ? "text-blue-700" : "text-gray-500"}`}>
              OTP Verification
            </span>
          </div>
        </div>

        {/* Card */}
        <div className="w-full max-w-2xl">
          {step === "email"
            ? <EmailStep initialEmail={initialEmail} onSend={handleSendOtp} beforeSend={handleBeforeSend} />
            : <OtpStep email={email} onVerify={handleVerify} onResend={() => sendOtp(email)} />
          }

          {step === "otp" && (
            <button onClick={() => setStep("email")}
              className="w-full text-center text-base text-gray-500 hover:text-blue-600 mt-4 transition">
              Change email
            </button>
          )}

          <div className="text-center mt-5">
            <p className="text-lg text-gray-500">
              New student?{" "}
              <button onClick={() => navigate("/student-registration")}
                className="text-blue-600 font-semibold hover:underline">
                Register here
              </button>
            </p>
          </div>

          <div className="text-center mt-4">
            <button onClick={() => navigate("/")}
              className="inline-flex items-center gap-1.5 text-base text-blue-600 font-semibold hover:underline transition">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Back to Home
            </button>
          </div>
        </div>

        <p className="text-base text-gray-500 mt-8">
          © {new Date().getFullYear()} University of Sri Jayewardenepura
        </p>
      </div>

      {/* No Account Found — popup modal. Overlays the login page (position:
          fixed) instead of navigating away, so the user stays put. */}
      {notRegistered && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="no-account-title"
          onClick={closeNotRegistered}
        >
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close (X) */}
            <button
              type="button"
              onClick={closeNotRegistered}
              aria-label="Close"
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-amber-50">
              <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 id="no-account-title" className="text-xl font-bold text-gray-900 mb-1">No Account Found</h2>
            <p className="text-sm text-gray-500 mb-1">No registration exists for</p>
            <p className="text-sm font-semibold text-blue-700 mb-5 break-all">{email}</p>
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-4 mb-5">
              <p className="text-base font-semibold text-amber-700">Please register to login.</p>
            </div>
            <button
              onClick={() => navigate("/student-registration", { state: { email } })}
              className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 rounded-lg text-sm transition"
            >
              Register Now
            </button>
          </div>
        </div>
      )}

      {/* Select a Club — shown right here on the login page (no /select-club
          redirect) when a coach with 2+ clubs verifies. */}
      {clubSelectData && (
        <ClubSelectModal member={clubSelectData.member} clubs={clubSelectData.clubs} navigate={navigate} />
      )}
    </AuthShell>
  );
}